import { defineStore } from 'pinia';
import { ref, computed, readonly } from 'vue';
import type {
  UserSession,
  AuthenticationResult,
  UserProfile,
  AuthError,
  TelegramUser,
  ImpersonationContext,
  RecentImpersonation,
} from '../types/auth';
import { authApi } from '../services/auth-api';
import { storageService } from '../services/storage';

// Store State Types - using imported types from @/types/auth

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<UserSession | null>(null);
  const loading = ref(false);
  const error = ref<AuthError | null>(null);
  const impersonationContext = ref<ImpersonationContext | null>(null);
  const recentImpersonations = ref<RecentImpersonation[]>([]);
  const lastActivity = ref<Date | null>(null);
  const sessionTimeoutWarning = ref(false);
  const isInitialized = ref(false);

  // Getters
  const isAuthenticated = computed(() => !!user.value && user.value.isAuthenticated);

  const userProfile = computed(() => {
    if (!user.value) return null;
    return {
      telegramId: user.value.telegramId,
      username: user.value.username,
      firstName: user.value.firstName,
      lastName: user.value.lastName,
      photoUrl: user.value.photoUrl,
    };
  });

  const tenantContext = computed(() => user.value?.tenantContext || null);

  const quotaUsage = computed(() => {
    // This would be populated from API calls
    return {
      vectorDocuments: { current: 0, limit: 1000, percentage: 0 },
      apiCalls: { currentHour: 0, limit: 100, percentage: 0 },
      storage: { currentBytes: 0, limitBytes: 100_000_000, percentage: 0 },
      conversations: { currentDay: 0, limit: 50, percentage: 0 },
      toolExecutions: { currentHour: 0, limit: 200, percentage: 0 },
    };
  });

  const timeToExpiry = computed(() => {
    if (!user.value?.expiresAt) return null;
    const now = new Date();
    const expiresAt = new Date(user.value.expiresAt);
    return Math.max(0, expiresAt.getTime() - now.getTime());
  });

  const isSessionExpiring = computed(() => {
    const time = timeToExpiry.value;
    return time !== null && time <= 10 * 60 * 1000; // 10 minutes
  });

  const canImpersonate = computed(
    () => user.value?.permissions?.includes('admin:impersonate') || false
  );

  const isImpersonating = computed(() => !!impersonationContext.value);

  const impersonatedUser = computed(() =>
    impersonationContext.value ? impersonationContext.value.impersonatedUser : null
  );

  // Actions
  const initialize = async (): Promise<void> => {
    if (isInitialized.value) return;

    loading.value = true;
    error.value = null;

    try {
      // First check for stored session and validate it
      const storedSession = storageService.getSession();
      if (storedSession && storageService.isSessionValid()) {
        console.log('Found valid stored session, validating with server...');

        // Set user immediately for UI responsiveness
        user.value = {
          ...storedSession,
          userId: storedSession.userId,
          telegramId: storedSession.telegramId,
          sessionId: storedSession.sessionId,
          createdAt: storedSession.createdAt,
          expiresAt: storedSession.expiresAt,
          lastActivity: storedSession.lastActivity,
          isAuthenticated: true,
          tenantContext: storedSession.tenantContext || undefined,
        };

        // Validate session with server in background
        const isValid = await validateSession();
        if (!isValid) {
          console.log('Stored session validation failed, clearing session');
          user.value = null;
          storageService.clearSession();
        } else {
          console.log('Session validated successfully');
          lastActivity.value = new Date();
        }
      }

      // If no valid session, check for auth token from Telegram
      if (!user.value) {
        const authToken = storageService.getAuthToken();
        if (authToken) {
          console.log('Found auth token, attempting authentication...');
          const authResult = await authenticateWithToken(authToken);
          if (authResult) {
            console.log('Authentication successful - keeping persistent token');
          } else {
            console.log('Authentication failed, keeping persistent token for retry');
          }
        }
      }

      isInitialized.value = true;
    } catch (err) {
      console.error('Failed to initialize auth store:', err);
      error.value = {
        type: 'UNKNOWN',
        message: 'Failed to initialize authentication',
        code: 'INIT_ERROR',
        timestamp: new Date(),
        retryable: true,
      };
      // Clear potentially corrupted session
      user.value = null;
      storageService.clearSession();
    } finally {
      loading.value = false;
    }
  };

  const login = async (token: string): Promise<AuthenticationResult> => {
    loading.value = true;
    error.value = null;

    try {
      const result = await authApi.login({ token });

      if (result.success && result.session) {
        // Ensure the session has the required fields for authentication
        const sessionData: UserSession = {
          ...result.session,
          isAuthenticated: true,
          expiresAt: new Date(result.expiresAt || result.session.expiresAt),
          createdAt: new Date(result.session.createdAt),
          lastActivity: new Date(result.session.lastActivity || new Date()),
          // Ensure tenant context is properly set
          tenantContext: result.session.tenantContext || undefined,
        };

        user.value = sessionData;
        lastActivity.value = new Date();
        storageService.setSession(sessionData);

        console.log(
          'User authenticated successfully:',
          sessionData.firstName,
          sessionData.username
        );
        console.log('Tenant context:', sessionData.tenantContext);

        return {
          success: true,
          session: sessionData,
          redirectUrl: '/',
        };
      } else {
        console.error('Authentication failed:', result.error);
        error.value = {
          type: 'TOKEN_INVALID',
          message: result.error || 'Invalid authentication token',
          code: 'LOGIN_FAILED',
          timestamp: new Date(),
          retryable: false,
        };
        return { success: false, error: error.value?.message || 'Authentication failed' };
      }
    } catch (err: unknown) {
      const errorContext = err as Error;
      console.error('Login error:', errorContext);
      error.value = {
        type: 'UNKNOWN',
        message: errorContext.message || 'Authentication failed',
        code: 'LOGIN_ERROR',
        timestamp: new Date(),
        retryable: true,
      };
      return { success: false, error: error.value?.message || 'Authentication failed' };
    } finally {
      loading.value = false;
    }
  };

  const authenticateWithToken = async (token: string): Promise<boolean> => {
    try {
      console.log('Authenticating with token...');
      const result = await login(token);
      console.log('Authentication result:', result.success);

      if (result.success) {
        // Keep persistent auth token - don't clear it
        console.log('Authentication successful, preserving persistent auth token');
      }

      return result.success;
    } catch (err) {
      console.error('Token authentication error:', err);
      return false;
    }
  };

  const logout = async (allSessions = false): Promise<void> => {
    loading.value = true;
    error.value = null;

    try {
      if (user.value?.sessionId) {
        await authApi.logout({
          sessionId: user.value.sessionId,
          allSessions,
        });
      }
    } catch (err) {
      console.error('Logout error:', err);
      // Continue with local logout even if API call fails
    } finally {
      // Clear local state but preserve auth token unless explicitly requested
      user.value = null;
      lastActivity.value = null;
      sessionTimeoutWarning.value = false;
      storageService.clearSession();
      // Only clear auth token if this is a complete logout (not just session expiry)
      if (allSessions) {
        storageService.clearAuthToken();
        console.log('Complete logout: cleared auth token');
      } else {
        console.log('Session logout: preserved auth token for re-authentication');
      }
      loading.value = false;
    }
  };

  const refreshSession = async (): Promise<boolean> => {
    if (!user.value?.sessionId) return false;

    try {
      const result = await authApi.refreshSession({
        sessionId: user.value.sessionId,
        refreshToken: user.value.sessionId, // Simplified - would use actual refresh token
      });

      if (result.success && result.session) {
        // Ensure session has proper authentication state
        const sessionData: UserSession = {
          ...result.session,
          isAuthenticated: true,
          expiresAt: new Date(result.session.expiresAt),
          createdAt: new Date(result.session.createdAt),
          lastActivity: new Date(result.session.lastActivity || new Date()),
          tenantContext: result.session.tenantContext || user.value?.tenantContext || undefined,
        };

        user.value = sessionData;
        lastActivity.value = new Date();
        storageService.setSession(sessionData);
        sessionTimeoutWarning.value = false;
        return true;
      }

      return false;
    } catch (err) {
      console.error('Session refresh failed:', err);
      return false;
    }
  };

  const validateSession = async (): Promise<boolean> => {
    if (!user.value) return false;

    try {
      const result = await authApi.validateSession();

      if (result.valid && result.session) {
        // Ensure session has proper authentication state
        const sessionData: UserSession = {
          ...result.session,
          isAuthenticated: true,
          expiresAt: new Date(result.session.expiresAt),
          createdAt: new Date(result.session.createdAt),
          lastActivity: new Date(result.session.lastActivity || new Date()),
          tenantContext: result.session.tenantContext || user.value?.tenantContext || undefined,
        };

        user.value = sessionData;
        lastActivity.value = new Date();
        storageService.setSession(sessionData);
        return true;
      } else if (result.needsRefresh) {
        console.log('Session needs refresh, attempting to refresh...');
        return await refreshSession();
      } else {
        // Session is invalid - clear session but preserve auth token
        console.log('Session validation failed - session invalid, clearing session only');
        user.value = null;
        storageService.clearSession();
        return false;
      }
    } catch (err) {
      console.error('Session validation failed:', err);
      // Don't auto-logout or clear tokens on network errors - might be temporary
      console.log('Network error during session validation, preserving tokens for retry');
      return false;
    }
  };

  const updateProfile = async (updates: Partial<UserProfile>): Promise<UserProfile | null> => {
    if (!user.value) return null;

    loading.value = true;
    error.value = null;

    try {
      const updatedProfile = await authApi.updateProfile(updates);

      // Update local user data
      if (updatedProfile && user.value) {
        const updatedSession: UserSession = {
          ...user.value,
          username: updatedProfile.username,
          firstName: updatedProfile.firstName || user.value.firstName, // Ensure firstName is never undefined
          lastName: updatedProfile.lastName,
          photoUrl: updatedProfile.photoUrl,
          isAuthenticated: true,
          tenantContext: user.value.tenantContext,
        };

        user.value = updatedSession;
        storageService.setSession(updatedSession);
      }

      return updatedProfile;
    } catch (err: unknown) {
      const errorContext = err as Error;
      console.error('Profile update failed:', errorContext);
      error.value = {
        type: 'UNKNOWN',
        message: errorContext.message || 'Failed to update profile',
        code: 'PROFILE_UPDATE_ERROR',
        timestamp: new Date(),
        retryable: true,
      };
      return null;
    } finally {
      loading.value = false;
    }
  };

  const updateLastActivity = (): void => {
    lastActivity.value = new Date();

    // Reset session timeout warning if user is active
    if (sessionTimeoutWarning.value) {
      sessionTimeoutWarning.value = false;
    }
  };

  const setSessionTimeoutWarning = (show: boolean): void => {
    sessionTimeoutWarning.value = show;
  };

  const clearError = (): void => {
    error.value = null;
  };

  const checkQuotaUsage = async (): Promise<void> => {
    if (!user.value) return;

    try {
      await authApi.getQuotaUsage();
      // Update quota usage in user context or separate reactive ref
      // This would update the quotaUsage computed property
    } catch (err) {
      console.error('Failed to fetch quota usage:', err);
    }
  };

  const getTelegramAuthUrl = (): string => {
    // This would generate a URL to start the Telegram bot authentication
    const botUsername =
      (import.meta as ImportMeta & { env: { VITE_TELEGRAM_BOT_USERNAME?: string } }).env
        .VITE_TELEGRAM_BOT_USERNAME || 'your_bot';
    return `https://t.me/${botUsername}?start=auth`;
  };

  const handleTelegramCallback = async (
    telegramData: TelegramUser
  ): Promise<AuthenticationResult> => {
    loading.value = true;
    error.value = null;

    try {
      const result = await authApi.telegramAuth(telegramData);

      if (result.success && result.session) {
        // Ensure session has proper authentication state
        const sessionData: UserSession = {
          ...result.session,
          isAuthenticated: true,
          expiresAt: new Date(result.session.expiresAt),
          createdAt: new Date(result.session.createdAt),
          lastActivity: new Date(result.session.lastActivity || new Date()),
          tenantContext: result.session.tenantContext || undefined,
        };

        user.value = sessionData;
        lastActivity.value = new Date();
        storageService.setSession(sessionData);
      }

      return result;
    } catch (err: unknown) {
      const errorContext = err as Error;
      error.value = {
        type: 'UNKNOWN',
        message: errorContext.message || 'Telegram authentication failed',
        code: 'TELEGRAM_AUTH_ERROR',
        timestamp: new Date(),
        retryable: true,
      };
      return { success: false, error: error.value?.message || 'Failed to clear sessions' };
    } finally {
      loading.value = false;
    }
  };

  // Session monitoring
  const startSessionMonitoring = (): void => {
    // Check session validity every 5 minutes
    setInterval(
      async () => {
        if (user.value && isAuthenticated.value) {
          const isValid = await validateSession();
          if (!isValid) {
            console.log('Session expired during monitoring, initiating graceful logout');
            await handleSessionExpired();
          }
        }
      },
      5 * 60 * 1000
    );

    // Check for session timeout warning every minute
    setInterval(() => {
      if (isSessionExpiring.value && !sessionTimeoutWarning.value) {
        sessionTimeoutWarning.value = true;
      }
    }, 60 * 1000);
  };

  // Handle session expiration gracefully
  const handleSessionExpired = async (): Promise<void> => {
    try {
      // First try to refresh the session
      const refreshed = await refreshSession();
      if (refreshed) {
        console.log('Session successfully refreshed after expiration');
        return;
      }

      // If refresh fails, try re-authentication with persistent token
      const authToken = storageService.getAuthToken();
      if (authToken) {
        console.log('Session refresh failed, attempting re-authentication with persistent token');
        const reauth = await authenticateWithToken(authToken);
        if (reauth) {
          console.log('Re-authentication successful');
          return;
        }
      }

      // If all fails, perform logout but keep token
      console.log('Session refresh and re-auth failed, performing logout but keeping token');
      await logout(false); // Don't clear token

      // Store current route as intended route if it's a protected route
      if (typeof window !== 'undefined') {
        const currentPath = window.location.pathname;
        const currentQuery = Object.fromEntries(new URLSearchParams(window.location.search));

        if (currentPath !== '/auth' && currentPath !== '/logout') {
          storageService.storeCurrentAsIntended({
            path: currentPath,
            query: currentQuery,
          });
        }

        // Redirect to auth page
        window.location.href = '/auth';
      }
    } catch (err) {
      console.error('Error handling session expiration:', err);
      // Force logout and redirect as fallback
      await logout();
      if (typeof window !== 'undefined') {
        window.location.href = '/auth';
      }
    }
  };

  // Auto-refresh session when close to expiry
  const autoRefreshSession = (): void => {
    setInterval(
      async () => {
        if (user.value && isSessionExpiring.value && !sessionTimeoutWarning.value) {
          const refreshed = await refreshSession();
          if (refreshed) {
            console.log('Session automatically refreshed');
          }
        }
      },
      2 * 60 * 1000
    ); // Check every 2 minutes
  };

  const uploadProfilePhoto = async (formData: FormData): Promise<void> => {
    loading.value = true;
    error.value = null;

    try {
      const result = await authApi.uploadProfilePhoto(formData);
      if (result.success && result.user) {
        // Update user profile with new photo URL
        if (user.value) {
          user.value = {
            ...user.value,
            photoUrl: result.user.photoUrl || result.user.profilePhotoUrl,
          };
          storageService.setSession(user.value);
        }
      }
    } catch (err: unknown) {
      const errorContext = err as Error;
      error.value = {
        type: 'UNKNOWN',
        message: errorContext.message || 'Failed to upload profile photo',
        code: 'UPLOAD_ERROR',
        timestamp: new Date(),
        retryable: true,
      };
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const deleteAccount = async (): Promise<void> => {
    loading.value = true;
    error.value = null;

    try {
      const result = await authApi.deleteAccount();
      if (result.success) {
        // Clear all local data
        user.value = null;
        lastActivity.value = null;
        sessionTimeoutWarning.value = false;
        storageService.clearAll();
      }
    } catch (err: unknown) {
      const errorContext = err as Error;
      error.value = {
        type: 'UNKNOWN',
        message: errorContext.message || 'Failed to delete account',
        code: 'DELETE_ERROR',
        timestamp: new Date(),
        retryable: false,
      };
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const refreshUserData = async (): Promise<boolean> => {
    if (!user.value?.sessionId) {
      console.log('No active session to refresh');
      return false;
    }

    loading.value = true;
    try {
      const response = await authApi.validateSession();

      if (response.valid && response.session) {
        // Update user data with latest information
        user.value = {
          ...response.session,
          userId: user.value.userId,
          telegramId: user.value.telegramId,
          sessionId: user.value.sessionId,
          createdAt: user.value.createdAt,
          expiresAt: user.value.expiresAt,
          lastActivity: user.value.lastActivity,
          isAuthenticated: true,
        };

        // Update storage
        storageService.setSession(user.value);

        console.log('User data refreshed successfully');
        return true;
      } else {
        console.log('Session validation failed during refresh');
        return false;
      }
    } catch (err) {
      console.error('Failed to refresh user data:', err);
      return false;
    } finally {
      loading.value = false;
    }
  };

  // Development utilities
  const testTokenAuthentication = async (token: string): Promise<boolean> => {
    if ((import.meta as ImportMeta & { env: { PROD?: boolean } }).env.PROD) {
      console.warn('testTokenAuthentication is only available in development');
      return false;
    }

    console.log('Testing token authentication:', token.substring(0, 10) + '...');
    storageService.setAuthToken(token);
    return await authenticateWithToken(token);
  };

  const getAuthDebugInfo = () => {
    if ((import.meta as ImportMeta & { env: { PROD?: boolean } }).env.PROD) return null;

    return {
      isInitialized: isInitialized.value,
      isAuthenticated: isAuthenticated.value,
      loading: loading.value,
      error: error.value,
      user: user.value,
      lastActivity: lastActivity.value,
      sessionTimeoutWarning: sessionTimeoutWarning.value,
      timeToExpiry: timeToExpiry.value,
      isSessionExpiring: isSessionExpiring.value,
      quotaUsage: quotaUsage.value,
    };
  };

  // Impersonation methods
  const startImpersonation = async (userId: string, reason?: string): Promise<boolean> => {
    loading.value = true;
    error.value = null;

    try {
      const response = await fetch('/api/admin/impersonation/start', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user.value?.sessionId}`,
        },
        body: JSON.stringify({
          targetUserId: userId,
          reason,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to start impersonation');
      }

      const data = await response.json();

      // Update impersonation context
      impersonationContext.value = {
        isImpersonating: true,
        adminUserId: user.value?.userId || '',
        impersonatedUser: data.targetUser,
        sessionId: data.sessionId,
        startedAt: new Date(data.startedAt),
      };

      // Store impersonation token
      localStorage.setItem('impersonation_token', data.impersonationToken);

      console.log('Impersonation started successfully');
      return true;
    } catch (err) {
      console.error('Failed to start impersonation:', err);
      error.value = {
        type: 'UNKNOWN',
        message: err instanceof Error ? err.message : 'Failed to start impersonation',
        code: 'IMPERSONATION_START_FAILED',
        timestamp: new Date(),
        retryable: true,
      };
      return false;
    } finally {
      loading.value = false;
    }
  };

  const endImpersonation = async (): Promise<boolean> => {
    loading.value = true;
    error.value = null;

    try {
      const sessionId = impersonationContext.value?.sessionId;
      if (!sessionId) {
        throw new Error('No active impersonation session');
      }

      const response = await fetch('/api/admin/impersonation/end', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user.value?.sessionId}`,
        },
        body: JSON.stringify({ sessionId }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to end impersonation');
      }

      // Clear impersonation context
      impersonationContext.value = null;
      localStorage.removeItem('impersonation_token');

      // Refresh recent impersonations
      await getRecentImpersonations();

      console.log('Impersonation ended successfully');
      return true;
    } catch (err) {
      console.error('Failed to end impersonation:', err);
      error.value = {
        type: 'UNKNOWN',
        message: err instanceof Error ? err.message : 'Failed to end impersonation',
        code: 'IMPERSONATION_END_FAILED',
        timestamp: new Date(),
        retryable: true,
      };
      return false;
    } finally {
      loading.value = false;
    }
  };

  const getRecentImpersonations = async (limit = 10): Promise<RecentImpersonation[]> => {
    try {
      const response = await fetch(`/api/admin/impersonation/recent?limit=${limit}`, {
        headers: {
          Authorization: `Bearer ${user.value?.sessionId}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch recent impersonations');
      }

      const data = await response.json();
      recentImpersonations.value = data.map((item: any) => ({
        ...item,
        startedAt: new Date(item.startedAt),
        endedAt: item.endedAt ? new Date(item.endedAt) : undefined,
      }));

      return recentImpersonations.value;
    } catch (err) {
      console.error('Failed to get recent impersonations:', err);
      return [];
    }
  };

  const validateImpersonationToken = async (): Promise<boolean> => {
    const impersonationToken = localStorage.getItem('impersonation_token');
    if (!impersonationToken || !user.value) {
      return false;
    }

    try {
      const response = await fetch('/api/admin/impersonation/validate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user.value?.sessionId}`,
        },
        body: JSON.stringify({ impersonationToken }),
      });

      if (!response.ok) {
        // Invalid token, clear it
        localStorage.removeItem('impersonation_token');
        impersonationContext.value = null;
        return false;
      }

      const data = await response.json();

      // Restore impersonation context
      impersonationContext.value = {
        isImpersonating: true,
        adminUserId: data.adminUserId,
        impersonatedUser: data.targetUser,
        sessionId: data.sessionId,
        startedAt: new Date(data.startedAt),
      };

      return true;
    } catch (error) {
      console.error('Failed to validate impersonation token:', error);
      localStorage.removeItem('impersonation_token');
      impersonationContext.value = null;
      return false;
    }
  };

  // Initialize session monitoring when store is created
  if (typeof window !== 'undefined') {
    startSessionMonitoring();
    autoRefreshSession();
  }

  return {
    // State
    user: readonly(user),
    loading: readonly(loading),
    error: readonly(error),
    impersonationContext: readonly(impersonationContext),
    recentImpersonations: readonly(recentImpersonations),
    lastActivity: readonly(lastActivity),
    sessionTimeoutWarning: readonly(sessionTimeoutWarning),
    isInitialized: readonly(isInitialized),

    // Getters
    isAuthenticated,
    userProfile,
    tenantContext,
    quotaUsage,
    timeToExpiry,
    isSessionExpiring,
    canImpersonate,
    isImpersonating,
    impersonatedUser,

    // Actions
    initialize,
    login,
    authenticateWithToken,
    logout,
    refreshSession,
    validateSession,
    updateProfile,
    updateLastActivity,
    setSessionTimeoutWarning,
    clearError,
    checkQuotaUsage,
    getTelegramAuthUrl,
    handleTelegramCallback,
    uploadProfilePhoto,
    deleteAccount,
    refreshUserData,
    startImpersonation,
    endImpersonation,
    getRecentImpersonations,
    validateImpersonationToken,

    // Development utilities
    testTokenAuthentication,
    getAuthDebugInfo,

    // Token management utilities
    hasValidToken: () => storageService.hasAuthToken(),
    getTokenInfo: () => storageService.getAuthTokenInfo(),
    clearPersistentToken: () => {
      storageService.clearAuthToken();
      console.log('Persistent auth token cleared');
    },
    refreshWithToken: async () => {
      const token = storageService.getAuthToken();
      if (token) {
        console.log('Attempting re-authentication with persistent token');
        return await authenticateWithToken(token);
      }
      return false;
    },
  };
});
